import { useMemo } from 'react';
import { format } from 'date-fns';
import { Trash2 } from 'lucide-react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { IconButton } from '@/components/ui/button.tsx';
import { JawsScanSummaryResponse } from '@/schema/jawsScan';
import type { Status as ScanStatus } from '@/schema/jawsScan';
import { usePatientDetailStore } from '@patientMenu/hooks/usePatientDetailStore';
import { cn } from '@/lib/utils';

export interface PatientScanListProps {
  scansSummary: JawsScanSummaryResponse[];
  className?: string;
  /** Hides the destructive action for viewers that only read the case (the admin console). */
  readOnly?: boolean;
  /** Takes over from the clinic URL when the list is mounted outside the patient routes. */
  onSelect?: (scan: JawsScanSummaryResponse) => void;
}

export default function PatientScanList({ scansSummary, className, readOnly, onSelect }: PatientScanListProps) {
  const { setSelectedScan, selectedScan } = usePatientDetailStore();
  const { patientId } = useParams<{ patientId: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  const handleScanSelect = (scanId: string) => {
    const nextScan = scansSummary.find((scan) => scan.id === scanId);
    if (!nextScan) {
      return;
    }

    if (!selectedScan || selectedScan.id !== nextScan.id) {
      setSelectedScan(nextScan);
    }

    if (onSelect) {
      onSelect(nextScan);
      return;
    }

    if (patientId) {
      navigate(`/patients/${patientId}/scans/${scanId}`);
    }
  };

  const sortedScans = useMemo(
    () => [...scansSummary].sort((a, b) => new Date(b.scanDate).getTime() - new Date(a.scanDate).getTime()),
    [scansSummary],
  );

  const statusLabel = (status: ScanStatus | null) => {
    switch (status) {
      case 'Scanned':
        return 'Scanned';
      case 'Segmented':
        return 'Segmented';
      case 'Corrected':
        return 'Corrected';
      case 'Printing':
        return 'Printing';
      case 'Braces Created':
        return 'Braces created';
      case 'Printed':
        return 'Printed';
      case 'Empty':
        return 'Not started';
      default:
        return 'Unknown';
    }
  };

  return (
    <section className={cn('flex h-full min-h-0 w-full flex-col gap-3 rounded-3xl border border-content-highlight/35 bg-content-dark/45 p-4 text-white shadow-inner shadow-primary/10 backdrop-blur lg:p-5', className)}>
      <header className="shrink-0 space-y-1">
        <p className="text-xs font-semibold uppercase tracking-[0.3em] text-white/60">Scans</p>
        <h3 className="text-lg font-semibold tracking-tight">Patient scans</h3>
        <p className="text-sm text-white/60">{sortedScans.length} {sortedScans.length === 1 ? 'scan' : 'scans'} available</p>
      </header>

      <div className="min-h-0 flex-1 space-y-3 overflow-y-auto pr-1">
        {sortedScans.map((scan) => {
          const isActive = selectedScan?.id === scan.id;
          return (
            <button
              key={scan.id}
              type="button"
              onClick={() => handleScanSelect(scan.id)}
              className={cn(
                'group relative flex w-full items-center justify-between gap-3 rounded-2xl border px-4 py-3 text-left transition duration-200',
                isActive
                  ? 'border-primary/70 bg-primary/15 shadow-lg shadow-primary/20'
                  : 'border-content-highlight/40 bg-content-dark/60 hover:border-primary/40 hover:bg-primary/10',
              )}
            >
              <div className="flex flex-col gap-1">
                <span className="text-sm font-semibold text-white">{format(new Date(scan.scanDate), 'MMM d, yyyy')}</span>
                <span className="text-xs text-white/55">{statusLabel(scan.status)}</span>
              </div>
              {!readOnly && (
                <IconButton
                  variant="ghost-danger"
                  size="icon"
                  className="rounded-xl border border-red-500/40 bg-red-500/15"
                  icon={Trash2}
                  onClick={(event) => {
                    event.stopPropagation();
                    navigate(`/patients/${scan.patientId}/scans/${scan.id}/delete`, { state: { backgroundLocation: location } });
                  }}
                />
              )}
            </button>
          );
        })}
      </div>
    </section>
  );
}
